import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Configure axios defaults
axios.defaults.baseURL = API_BASE_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    // Set up axios interceptor for token - use localStorage directly to avoid timing issues
    const interceptor = axios.interceptors.request.use(
      (config) => {
        const storedToken = localStorage.getItem('token');
        if (storedToken) {
          config.headers.Authorization = `Bearer ${storedToken}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Set up response interceptor for 401 errors
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Only logout if we're not on the login page
          if (window.location.pathname !== '/login') {
            logout();
          }
        }
        return Promise.reject(error);
      }
    );

    // Load user if token exists
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      loadUser();
    } else {
      setLoading(false);
    }

    return () => {
      axios.interceptors.request.eject(interceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadUser = async () => {
    try {
      const response = await axios.get('/auth/me');
      setUser(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load user:', error);
      // Only logout if we have a token but it's invalid (not just missing)
      if (localStorage.getItem('token') && error.response?.status === 401) {
        logout();
      } else {
        setLoading(false);
      }
    }
  };

  const login = async (email, password, twoFactorCode = null) => {
    try {
      const response = await axios.post('/auth/login', {
        email,
        password,
        two_factor_code: twoFactorCode,
      });
      const { access_token } = response.data;
      localStorage.setItem('token', access_token);
      setToken(access_token);
      // Wait a moment for the token to be set in the interceptor
      await new Promise(resolve => setTimeout(resolve, 100));
      await loadUser();
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Login failed',
      };
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
  };

  const hasRole = (roles) => {
    if (!user) return false;
    const raw = user.role;
    const normalized =
      raw == null ? '' : typeof raw === 'string' ? raw.toLowerCase() : String(raw).toLowerCase();
    return roles.some((r) => r === normalized || r === raw);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        hasRole,
        token,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

